import React from 'react';
import { ArrowRight, TrendingUp, DollarSign, Target } from 'lucide-react';
import type { PropertyAnalysisResult } from '../../types/calculations';
import { SectionTitle } from '../Luxury';

interface ZandiDealImpactProps {
    data: PropertyAnalysisResult;
}

// Formatting helpers
const fmt$ = (v: number | null | undefined): string => {
    if (v == null) return '--';
    return `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const fmtPct = (v: number | null | undefined): string => {
    if (v == null) return '--';
    return `${(v * 100).toFixed(1)}%`;
};

const fmtScore = (v: number | null | undefined): string => {
    if (v == null) return '--';
    return v.toFixed(0);
};

/**
 * Zandi Deal Impact Narrative — Phase 65
 *
 * Bridges Zandi market intelligence to concrete dollar-and-cents impact.
 * Three insight cards: Rent Impact, CapEx Sizing Chain, Deal Outcome.
 * Pure presentation component — no state management needed.
 */
export const ZandiDealImpact: React.FC<ZandiDealImpactProps> = ({ data }) => {
    const transparency = data.rent_capex_transparency;
    const zandi = data.zandi_scoring;
    const returns = data.return_metrics;
    const verdict = data.deal_verdict;

    // Hide entire section if no transparency data
    if (!transparency) return null;

    const zandiEgi = zandi?.raw_data?.egi_per_unit;
    const marketRent = transparency.market_rent_per_unit;
    const compositeScore = zandi?.composite_score;

    // Find which rent source was selected and if Zandi is in the list
    const rentSources = transparency.rent_sources || [];
    const zandiSource = rentSources.find(s => s.name?.toLowerCase().includes('zandi'));
    const hasZandiInSources = !!zandiSource;

    // Rent gap between Zandi EGI and deal's market rent
    const rentGapDollar = zandiEgi != null && marketRent != null ? zandiEgi - marketRent : null;
    const rentGapPct = zandiEgi != null && marketRent != null && marketRent > 0
        ? ((zandiEgi - marketRent) / marketRent)
        : null;

    // CapEx chain — use same data sources as Tab 1 for consistency
    const capexAnalysis = data.capex_analysis;
    const opexBreakdown = data.opex_breakdown;

    const tier = transparency?.tier || '--';
    const opexRate = opexBreakdown?.total_rate ?? transparency?.opex_rate;
    const capexBudget = capexAnalysis?.total_budget ?? transparency?.capex_at_20_pct_roi;
    const capexFromSSOT = capexAnalysis?.total_budget != null;
    // Use Three-State NOI renovation_gain for consistency with Tab 1; fall back to transparency
    const noiUplift = data.three_state_noi?.renovation_gain ?? transparency.annual_noi_uplift;

    // Returns
    const irr = returns?.irr_5yr;
    const coc = returns?.cash_on_cash;
    const dealVerdict = verdict?.verdict;

    // Score strength classification
    const getStrength = (score: number | null | undefined): { label: string; color: string; bgColor: string } => {
        if (score == null) return { label: 'Unknown', color: 'text-stone-700', bgColor: 'bg-white/5' };
        if (score >= 75) return { label: 'Strong', color: 'text-emerald', bgColor: 'bg-emerald/10' };
        if (score >= 55) return { label: 'Moderate', color: 'text-amber-400', bgColor: 'bg-amber-400/10' };
        return { label: 'Weak', color: 'text-red-400', bgColor: 'bg-red-400/10' };
    };

    const strength = getStrength(compositeScore);

    // Deal Outcome narrative data
    const IRR_TARGET = 0.12;
    const failingMetrics = verdict?.failing_metrics || [];
    const passingMetrics = verdict?.passing_metrics || [];

    const metricFriendlyName: Record<string, string> = {
        irr_5yr: '5yr IRR',
        cash_on_cash: 'Cash-on-Cash',
        dscr: 'DSCR',
        ltv: 'LTV',
    };

    const friendlyMetric = (m: string) => metricFriendlyName[m] || m;

    // Find strongest and weakest Zandi factors for market context
    const getFactorHighlights = (): { strongest: string | null; weakest: string | null } => {
        if (!zandi) return { strongest: null, weakest: null };
        const factors = [
            { name: 'Rent Gap', score: zandi.rent_gap_score },
            { name: 'Affordability', score: zandi.mobi_score },
            { name: 'Momentum', score: zandi.momentum_score },
            { name: 'Low Vacancy', score: zandi.vacancy_score },
            { name: 'Valuation', score: zandi.valuation_score },
            { name: 'Permits', score: zandi.permit_score },
            { name: 'Spillover', score: zandi.spillover_score },
        ];
        const sorted = [...factors].sort((a, b) => b.score - a.score);
        const best = sorted[0];
        const worst = sorted[sorted.length - 1];
        return {
            strongest: best.score > 50 ? best.name : null,
            weakest: worst.score < 50 ? worst.name : null,
        };
    };

    // Momentum phase label from YoY data
    const getMomentumLabel = (): string | null => {
        const yoy = zandi?.raw_data?.yoy_change;
        if (yoy == null) return null;
        if (yoy >= 0.05) return 'accelerating';
        if (yoy >= 0.02) return 'growing';
        if (yoy >= -0.01) return 'stable';
        return 'decelerating';
    };

    // Narrative style based on verdict
    const narrativeStyle = dealVerdict === 'GO'
        ? 'bg-emerald/10 border-emerald/30'
        : dealVerdict === 'CONDITIONAL'
        ? 'bg-amber-400/10 border-amber-400/30'
        : dealVerdict === 'NO-GO'
        ? 'bg-red-400/10 border-red-400/30'
        : `${strength.bgColor} border-purple-500/20`;

    // Verdict badge colors
    const verdictStyles: Record<string, { bg: string; text: string }> = {
        'GO': { bg: 'bg-emerald/20', text: 'text-emerald' },
        'CONDITIONAL': { bg: 'bg-amber-400/20', text: 'text-amber-400' },
        'NO-GO': { bg: 'bg-red-400/20', text: 'text-red-400' },
    };

    return (
        <div className="bg-charcoal p-6 rounded-lg border border-purple-500/30">
            <div className="flex items-center gap-3 mb-6">
                <SectionTitle>What Zandi Means for YOUR Deal</SectionTitle>
            </div>
            <p className="text-sm text-stone-700 -mt-4 mb-6">
                How submarket intelligence translates to your specific investment outcomes.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ── Card 1: Rent Impact ── */}
                <div className="bg-graphite rounded-lg border border-slate-dark p-4 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-purple-500/20 rounded">
                            <DollarSign className="w-4 h-4 text-purple-400" />
                        </div>
                        <h4 className="text-sm font-semibold text-ivory uppercase tracking-wider">Rent Impact</h4>
                    </div>

                    {/* Zandi EGI vs Deal Rent */}
                    {zandiEgi != null && marketRent != null ? (
                        <div className="space-y-3 flex-1">
                            <div className="flex justify-between items-baseline">
                                <span className="text-sm text-stone-700">Zandi EGI</span>
                                <span className="font-mono text-sm text-purple-300">{fmt$(zandiEgi)}<span className="text-stone-700">/unit</span></span>
                            </div>
                            <div className="flex justify-between items-baseline">
                                <span className="text-sm text-stone-700">Deal Rent</span>
                                <span className="font-mono text-sm text-ivory">{fmt$(marketRent)}<span className="text-stone-700">/unit</span></span>
                            </div>
                            {rentGapDollar != null && rentGapPct != null && (
                                <div className={`p-2 rounded border ${
                                    rentGapDollar > 0
                                        ? 'bg-emerald/10 border-emerald/30'
                                        : rentGapDollar < 0
                                        ? 'bg-amber-400/10 border-amber-400/30'
                                        : 'bg-white/5 border-slate-dark'
                                }`}>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-stone-700">Rent Gap</span>
                                        <span className={`font-mono text-sm font-semibold ${
                                            rentGapDollar > 0 ? 'text-emerald' : rentGapDollar < 0 ? 'text-amber-400' : 'text-stone-700'
                                        }`}>
                                            {rentGapDollar > 0 ? '+' : ''}{fmt$(rentGapDollar)} ({rentGapPct > 0 ? '+' : ''}{(rentGapPct * 100).toFixed(1)}%)
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-sm text-stone-700 flex-1">Zandi EGI data not available for comparison.</div>
                    )}

                    {/* Rent Sources */}
                    <div className="mt-3 pt-3 border-t border-slate-dark">
                        <p className="text-sm text-stone-700 uppercase tracking-wider mb-2">Rent Sources</p>
                        {rentSources.length > 0 ? (
                            <div className="space-y-1">
                                {rentSources.map((src, i) => (
                                    <div key={i} className={`flex items-center justify-between px-2 py-1 rounded text-sm ${
                                        src.selected
                                            ? 'bg-gold/10 border border-gold/30'
                                            : src.name?.toLowerCase().includes('zandi')
                                            ? 'bg-purple-500/10 border border-purple-500/20'
                                            : 'bg-graphite'
                                    }`}>
                                        <span className={`${
                                            src.selected ? 'text-gold font-semibold' :
                                            src.name?.toLowerCase().includes('zandi') ? 'text-purple-300' :
                                            'text-stone-700'
                                        }`}>
                                            {src.name}
                                            {src.selected && <span className="ml-1 text-gold/70">(selected)</span>}
                                        </span>
                                        <span className="font-mono text-stone-700">{fmt$(src.value)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-stone-700">No rent source data available.</p>
                        )}
                        {!hasZandiInSources && rentSources.length > 0 && (
                            <p className="text-sm text-purple-400/60 mt-1 italic">Zandi EGI not used in rent estimate</p>
                        )}
                    </div>
                </div>

                {/* ── Card 2: CapEx Sizing Chain ── */}
                <div className="bg-graphite rounded-lg border border-slate-dark p-4 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-purple-500/20 rounded">
                            <TrendingUp className="w-4 h-4 text-purple-400" />
                        </div>
                        <h4 className="text-sm font-semibold text-ivory uppercase tracking-wider">CapEx Sizing Chain</h4>
                    </div>

                    {/* Stepped flow: Tier → OpEx → NOI → CapEx */}
                    <div className="space-y-2 flex-1">
                        {/* Step 1: Tier */}
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-purple-300">1</span>
                            </div>
                            <div className="flex-1 flex justify-between items-center">
                                <span className="text-sm text-stone-700">Submarket Tier</span>
                                <span className={`font-mono text-sm font-semibold ${
                                    tier === 'Tier1' ? 'text-emerald' : tier === 'Tier2' ? 'text-amber-400' : 'text-red-400'
                                }`}>{tier || '--'}</span>
                            </div>
                        </div>
                        <div className="ml-3 border-l border-purple-500/20 h-2" />

                        {/* Step 2: OpEx Rate */}
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-purple-300">2</span>
                            </div>
                            <div className="flex-1 flex justify-between items-center">
                                <span className="text-sm text-stone-700">OpEx Rate</span>
                                <span className="font-mono text-sm text-ivory">{opexRate != null ? `${(opexRate * 100).toFixed(0)}%` : '--'}</span>
                            </div>
                        </div>
                        <div className="ml-3 border-l border-purple-500/20 h-2" />

                        {/* Step 3: NOI Uplift — uses Three-State renovation_gain for Tab 1 consistency */}
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-purple-300">3</span>
                            </div>
                            <div className="flex-1 flex justify-between items-center">
                                <span className="text-sm text-stone-700">Renovation NOI Gain</span>
                                <span className="font-mono text-sm text-emerald">{fmt$(noiUplift)}<span className="text-stone-700">/yr</span></span>
                            </div>
                        </div>
                        <div className="ml-3 border-l border-purple-500/20 h-2" />

                        {/* Step 4: CapEx Budget */}
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-gold">4</span>
                            </div>
                            <div className="flex-1 flex justify-between items-center">
                                <span className="text-sm text-stone-700">CapEx Budget{capexFromSSOT && <span className="text-stone-700"> (SSOT tier budget)</span>}</span>
                                <span className="font-mono text-sm font-semibold text-gold">{fmt$(capexBudget)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Zandi connection callout */}
                    <div className="mt-3 pt-3 border-t border-slate-dark">
                        <div className={`p-2 rounded border ${strength.bgColor} border-purple-500/20`}>
                            <p className="text-sm text-stone-700">
                                <span className={`font-semibold ${strength.color}`}>{strength.label} submarket</span>
                                {compositeScore != null && (
                                    <span className="text-stone-700"> ({fmtScore(compositeScore)}/100)</span>
                                )}
                                {' '}
                                <ArrowRight className="w-3 h-3 inline text-stone-700" />
                                {' '}
                                <span className="text-ivory">{tier || '--'}</span> tier drives{' '}
                                <span className="font-mono text-ivory">{opexRate != null ? `${(opexRate * 100).toFixed(0)}%` : '--'}</span> OpEx rate
                            </p>
                        </div>
                    </div>
                </div>

                {/* ── Card 3: Deal Outcome ── */}
                <div className="bg-graphite rounded-lg border border-slate-dark p-4 flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-purple-500/20 rounded">
                            <Target className="w-4 h-4 text-purple-400" />
                        </div>
                        <h4 className="text-sm font-semibold text-ivory uppercase tracking-wider">Deal Outcome</h4>
                    </div>

                    {/* Return Metrics */}
                    <div className="space-y-3 flex-1">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-2 bg-obsidian rounded border border-slate-dark text-center">
                                <p className="text-sm text-stone-700">5yr IRR</p>
                                <p className={`text-lg font-mono font-bold ${
                                    irr != null && irr >= 0.15 ? 'text-emerald' :
                                    irr != null && irr >= 0.10 ? 'text-amber-400' :
                                    irr != null ? 'text-red-400' : 'text-stone-700'
                                }`}>
                                    {irr != null ? fmtPct(irr) : '--'}
                                </p>
                            </div>
                            <div className="p-2 bg-obsidian rounded border border-slate-dark text-center">
                                <p className="text-sm text-stone-700">Cash-on-Cash</p>
                                <p className={`text-lg font-mono font-bold ${
                                    coc != null && coc >= 0.08 ? 'text-emerald' :
                                    coc != null && coc >= 0.05 ? 'text-amber-400' :
                                    coc != null ? 'text-red-400' : 'text-stone-700'
                                }`}>
                                    {coc != null ? fmtPct(coc) : '--'}
                                </p>
                            </div>
                        </div>

                        {/* Verdict Badge */}
                        {dealVerdict && (
                            <div className="flex justify-center">
                                <span className={`inline-block px-3 py-1.5 text-sm font-bold uppercase tracking-wider rounded ${
                                    verdictStyles[dealVerdict]?.bg || 'bg-white/10'
                                } ${verdictStyles[dealVerdict]?.text || 'text-stone-700'}`}>
                                    {dealVerdict}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Narrative */}
                    <div className="mt-3 pt-3 border-t border-slate-dark">
                        <div className={`p-2 rounded border ${narrativeStyle}`}>
                            <div className="text-sm text-stone-700 leading-relaxed space-y-1">
                                {/* Opening line — verdict-aware */}
                                <p>
                                    {dealVerdict === 'GO' && compositeScore != null ? (
                                        <>
                                            Market tailwinds reinforce this deal.{' '}
                                            <span className={`font-semibold ${strength.color}`}>{strength.label} submarket</span>
                                            {' '}({fmtScore(compositeScore)}/100) delivers{' '}
                                            {irr != null ? `${fmtPct(irr)} IRR (GO threshold: ${fmtPct(IRR_TARGET)})` : 'solid fundamentals'}.
                                        </>
                                    ) : dealVerdict === 'CONDITIONAL' && compositeScore != null ? (
                                        <>
                                            Market is supportive but deal metrics need attention.{' '}
                                            <span className={`font-semibold ${strength.color}`}>{strength.label} submarket</span>
                                            {' '}({fmtScore(compositeScore)}/100){' '}
                                            {irr != null ? <>delivers {fmtPct(irr)} IRR (GO threshold: {fmtPct(IRR_TARGET)})</> : 'shows mixed signals'}.
                                        </>
                                    ) : dealVerdict === 'NO-GO' && compositeScore != null ? (
                                        <>
                                            Despite market conditions, this deal does not meet investment thresholds.{' '}
                                            <span className={`font-semibold ${strength.color}`}>{strength.label} submarket</span>
                                            {' '}({fmtScore(compositeScore)}/100){' '}
                                            {irr != null ? <>delivers {fmtPct(irr)} IRR (GO threshold: {fmtPct(IRR_TARGET)})</> : 'cannot overcome deal shortfalls'}.
                                        </>
                                    ) : compositeScore != null ? (
                                        <>
                                            <span className={`font-semibold ${strength.color}`}>{strength.label} submarket</span>
                                            {' '}({fmtScore(compositeScore)}/100){' '}
                                            {irr != null ? <>delivers {fmtPct(irr)} IRR (GO threshold: {fmtPct(IRR_TARGET)})</> : ''}.
                                        </>
                                    ) : (
                                        <span className="text-stone-700">Submarket score not available for narrative analysis.</span>
                                    )}
                                </p>

                                {/* Metric detail line */}
                                {failingMetrics.length > 0 && (
                                    <p className="text-red-400/80">
                                        Falling short: {failingMetrics.map(friendlyMetric).join(', ')}.
                                    </p>
                                )}
                                {passingMetrics.length > 0 && (
                                    <p className="text-emerald/80">
                                        Strengths: {passingMetrics.map(friendlyMetric).join(', ')}.
                                    </p>
                                )}

                                {/* Market context — factor highlights */}
                                {compositeScore != null && (() => {
                                    const { strongest, weakest } = getFactorHighlights();
                                    const momentum = getMomentumLabel();
                                    const parts: string[] = [];
                                    if (strongest) parts.push(`strongest factor: ${strongest}`);
                                    if (weakest) parts.push(`watch: ${weakest}`);
                                    if (momentum) parts.push(`market ${momentum}`);
                                    return parts.length > 0 ? (
                                        <p className="text-purple-300/70">
                                            Submarket drivers — {parts.join(', ')}.
                                        </p>
                                    ) : null;
                                })()}

                                {/* Rent gap context */}
                                {rentGapDollar != null && rentGapPct != null && (
                                    <p className={rentGapDollar > 0 ? 'text-emerald/70' : 'text-amber-400/70'}>
                                        {rentGapDollar > 0
                                            ? `Zandi EGI suggests +${fmt$(rentGapDollar)}/unit rent upside (${(rentGapPct * 100).toFixed(1)}% gap).`
                                            : `Deal rent exceeds Zandi EGI by ${fmt$(Math.abs(rentGapDollar))}/unit — validate rent assumptions.`
                                        }
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZandiDealImpact;
